import { DateTime } from '@ksyos/date-time';
import { Alert, FormGroup, Loader, Radio, RadioButton, RadioGroup, TextArea, TextInput } from '@ksyos/design-system';
import { useSelector } from 'react-redux';
import type { ICheckedTreatment } from '@common/interfaces/checkedTreatment';
import type { IReduxState } from '@frontend/configureStore';
import { MraAbcdScore } from '@common/enums/mraAbcdScore';
import { RadioGroupWithTextArea } from '@frontend/components/fields/RadioGroupWithTextArea';
import { hasActiveGgzbTherServiceConsumerRights } from '@frontend/services/api/serviceConsumerApiService';
import { isEmailDuplicateForPatient } from '@frontend/services/api/therapielandApiService';
import { getValidationErrorTranslation } from '@frontend/utils/getValidationErrorTranslation';
import type { ValidationErrors } from '../../../../../utils/validation';
import type { IRequest } from '../../../../interfaces/request';
import type { IDiagnosticReportAddEdit } from '../../../diagnosticReports/DiagnosticReportItemAddEdit';
import type { IComorbiditeitenKsyosMedicalCenter } from '../../OSASC1/v1/Osasc1V1DiagnosticReportAddEditCustomControlFields';
import { YesOrNo } from '../../../../../../common/enums/yesOrNo';
import { calculateBmi, getBmiCategorie } from '../../../../../../common/utils/bmi';
import {
    CpapBeleidOption,
    CpapOption,
    mateVanOsaOptions,
    mateVanZiektelastOptions,
    positieafhankelijkOptions,
    rijbevoegdheidBesprokenOptions,
    SleepRecorderType,
    SptOption,
} from '../../../../../../common/validation/customControls/somnology';
import { fetchJson } from '../../../../../store/fetch';
import { translateKey } from '../../../../../translate';
import { fetchActiveOsasqResendDate } from '../../../../../utils/fetchActiveOsasqResendDate';
import { CheckMarkButton } from '../../../../elements/buttons/checkMarkButton/CheckMarkButton';
import {
    ButtonContainer,
    FormDivider,
    FormInlineGroup,
    FormParagraph,
    HeaderDivider,
    IconOnClick,
    InputCheckbox,
    InputRadio,
    InputTextArea,
    NullableInputText,
} from '../../../../elements/common';
import { Component } from '../../../../elements/Component';
import { FormContextProvider } from '../../../../elements/formContext';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroup } from '../../../../elements/forms/HorizontalFormGroup';
import { HorizontalFormGroupRead } from '../../../../elements/forms/horizontalFormGroupRead/HorizontalFormGroupRead';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { InputTextCheckbox } from '../../../../elements/InputTextCheckbox';
import { TherapielandProgrammasModal } from '../../../../elements/TherapielandProgrammasModal';
import { CheckboxInput } from '../../../../fields/Checkbox';
import { DateField } from '../../../../fields/Date';
import { NumberInput } from '../../../../fields/Number';
import { RadioElementGroup } from '../../../../fields/RadioElementGroup';
import { RadioGroupField, RadioGroupInput } from '../../../../fields/RadioGroup';
import { TextField } from '../../../../fields/Text';
import { TextAreaField } from '../../../../fields/TextArea';

interface IBeleid {
    adviesLifestyleVerandering: boolean;
    adviesLifestyleVeranderingRemarks: string | null;
    beleidAndersText: string | null;
    cpap: boolean;
    cpapBeleidOption: CpapBeleidOption | null;
    cpapBeleidOptionRemark: string | null;
    cpapOption: string | null;
    cpapOptionRemark: string | null;
    doorverwijzen: boolean;
    doorverwijzenRemarks: string | null;
    mra: boolean;
    mraAbcdScore: MraAbcdScore | null;
    mraAbcdScoreRemark: string | null;
    spt: boolean;
    sptOption: SptOption | null;
    sptOptionRemark: string | null;
}

export interface IToesturenKsyosMedicalCenter {
    bewegen: boolean;
    leefstijl: boolean;
    lekker_slapen: boolean;
    ontspanning: boolean;
    piekeren: boolean;
    slaap_en_shifts: boolean;
    slaaprestrictie: boolean;
}

export interface IKsyosMedicalCenterCustomControlDataFields {
    aanvullendeAnamnese: string | null;
    aanvullendLichamelijkOnderzoek: string | null;
    adviesAanDeHuisarts: string | null;
    afwijkendHartritme: boolean;
    afwijkendHartritmeRemarks: string | null;
    andereComorbiditeiten: boolean | null;
    andereComorbiditeitenRemarks: string | null;
    behandeling1eLijnRemark: string | null;
    beleid: IBeleid;
    // from referral
    bmiCategorie: string | null;
    checkedTreatments?: ICheckedTreatment[];
    comorbiditeitenVolgensHuisarts: IComorbiditeitenKsyosMedicalCenter;
    dateConsult: string | null;
    emailAddress: string | null;
    gemiddeldeAhi: string | null;
    gemiddeldeOdi: string | null;
    gemiddeldeSaturatie: string | null;
    inefficienteSlaaphygiene: boolean;
    inefficienteSlaaphygieneRemarks: string | null;
    laagstGemetenSaturatie: string | null;
    // OSA Yes
    mateVanOsa: string | null;
    mateVanZiektelast: string | null;
    odi4: string | null;
    osa: boolean | null;
    osasq_resend_date: string | null;
    osasq_resend_week_dif: number | null;
    overigeRelevanteBevindingenAndersText: string | null;
    positieafhankelijk: string | null;
    positieafhankelijkRemarks: string | null;
    relevanteHypoxemie: boolean | null;
    relevanteHypoxemieRemarks: string | null;
    rijbevoegdheidBesproken: string | null;
    rijbevoegdheidBesprokenRemark: string | null;
    slechteSlaapefficientie: boolean;
    // both
    toesturen?: IToesturenKsyosMedicalCenter;
    typeSlaaprecorder: string;
    veelBewegingen: boolean;
    vervolgAfspraakMetSpecialistAndersText: string | null;
    volgensProtocol: boolean;
}

const osasc2PdfKeys: (keyof IKsyosMedicalCenterCustomControlDataFields)[] = [
    'gemiddeldeAhi',
    'gemiddeldeOdi',
    'odi4',
    'laagstGemetenSaturatie',
    'gemiddeldeSaturatie',
];

const adviesAanDeHuisartsOptions = [
    'Geen behandelindicatie',
    'Behandeling in de 1e lijn',
    'Doorverwijzen naar regionaal slaapcentrum',
    'Doorverwijzen naar KNO arts',
];

const sptOptionsDefaultRemarks = new Map<SptOption, string>([
    [SptOption.A, 'Na 3 maanden vervolgconsult. Begeleiding door OSAS consulent.'],
    [SptOption.B, 'Na 3 maanden vervolgonderzoek en een vervolgconsult. Begeleiding door OSAS consulent.'],
]);

const mraAbcdScoresDefaultRemarks = new Map<MraAbcdScore, string>([
    [MraAbcdScore.A, 'Na 3 maanden vervolgconsult met OSAS verpleegkundige.'],
    [MraAbcdScore.B, 'Na 3 maanden vervolgconsult met medisch specialist, geen controle slaaponderzoek.'],
    [MraAbcdScore.C, 'Na 3 maanden controle slaaponderzoek en een vervolgconsult met medisch specialist.'],
    [MraAbcdScore.D, 'Na 3 maanden controle slaaponderzoek en een vervolgconsult met medisch specialist.'],
]);

const cpapBeleidOptionsDefaultRemarks = new Map<CpapBeleidOption, string>([
    [
        CpapBeleidOption.diagnosticCourse,
        [
            'Vervolgconsult bij medisch specialist na 8 weken.',
            'Geen begeleiding van de OSAS consulent nodig.',
            'Stopverklaring of akkoordverklaring in het portaal van de leverancier wordt uitgevoerd door de polikliniek medewerker bij het verwerken van het spreekuur na “vervolgconsult” volgens ingevuld beleid.',
        ]
            .map((listElements) => listElements.replace(/^/, '\u2022 '))
            .join('\n'),
    ],
    [
        CpapBeleidOption.clearDiagnosis,
        [
            'De OSAS consulent voert na 4-6 weken een controle consult uit.',
            'Indien de therapie goed gaat kan de OSAS consulent de CPAP-therapie akkoord geven.',
        ]
            .map((listElements) => listElements.replace(/^/, '\u2022 '))
            .join('\n'),
    ],
    [
        CpapBeleidOption.clearDiagnosisWithUnpredictableEffectOfTreatment,
        [
            'De OSAS consulent voert na 4-6 weken een controle consult uit.',
            'Indien de therapie goed gaat meldt de OSAS-verpleegkundige de CPAP-therapie akkoord.',
            'Indien er problemen zijn of bij twijfel dan alsnog in week 8 een consult met de medisch specialist inplannen.',
        ]
            .map((listElements) => listElements.replace(/^/, '\u2022 '))
            .join('\n'),
    ],
    [
        CpapBeleidOption.complexTreatment,
        [
            'De OSAS consulent voert na 4-6 weken een controle consult uit.',
            'Na 8 weken wordt er een consult ingepland met de medisch specialist.',
        ]
            .map((listElements) => listElements.replace(/^/, '\u2022 '))
            .join('\n'),
    ],
]);

interface IOsasc2V2KsyosMedicalCenterCustomControlFieldsProps {
    errors: ValidationErrors;
    fields: IKsyosMedicalCenterCustomControlDataFields;
    handleChange: <K extends keyof IDiagnosticReportAddEdit>(
        name: K,
        value: IDiagnosticReportAddEdit[K]
    ) => Promise<void>;
    handleChangeCustomControl: <T = any>(values: Partial<T>) => Promise<void>;
    remark: string | null;
    request: IRequest;
    setButtonDisabled: (errorShown: boolean) => Promise<void>;
    validateOne: (name: keyof IDiagnosticReportAddEdit) => Promise<void>;
    validateOneCustomControl: (name: string, index?: number) => Promise<void>;
}

interface IOsasc2V2KsyosMedicalCenterCustomControlFieldsState {
    activeOsasqResendDate: Date | null;
    emailAddressErrorShown: boolean;
    hasActiveGgzbTherServiceConsumerRights: boolean;
    isLoading: boolean;
    therapielandProgrammaIsModalShown: boolean;
}

interface IInnerOsasc2V2KsyosMedicalCenterReduxPropsState {
    selectedEmploymentId: string;
}

export function Osasc2V2KsyosMedicalCenterCustomControlFields(
    props: IOsasc2V2KsyosMedicalCenterCustomControlFieldsProps
) {
    const selectedEmploymentId = useSelector((state: IReduxState) => state.authentication.selectedEmploymentId);

    return (
        <InnerOsasc2V2KsyosMedicalCenterCustomControlFields
            {...props}
            selectedEmploymentId={selectedEmploymentId}
        />
    );
}

class InnerOsasc2V2KsyosMedicalCenterCustomControlFields extends Component<
    IOsasc2V2KsyosMedicalCenterCustomControlFieldsProps & IInnerOsasc2V2KsyosMedicalCenterReduxPropsState,
    IOsasc2V2KsyosMedicalCenterCustomControlFieldsState
> {
    private readonly abortController = new AbortController();

    constructor(
        props: IOsasc2V2KsyosMedicalCenterCustomControlFieldsProps & IInnerOsasc2V2KsyosMedicalCenterReduxPropsState
    ) {
        super(props);

        this.state = {
            activeOsasqResendDate: null,
            therapielandProgrammaIsModalShown: false,
            emailAddressErrorShown: false,
            hasActiveGgzbTherServiceConsumerRights: true,
            isLoading: true,
        };
    }

    public async componentDidMount() {
        await this.setState({ isLoading: true });

        const asyncOperations = [
            fetchActiveOsasqResendDate(this.props.request.patientId),
            this.loadEmailPatient(),
            this.checkHasActiveGgzbTherServiceConsumerRights(),
        ];

        if (this.props.request.customControlData?.referralRequestId) {
            asyncOperations.push(
                this.loadReferralCustomControlData(this.props.request.customControlData.referralRequestId),
                this.getDataFromOsasPdfReports()
            );
        }

        const [activeOsasqResendDate] = (await Promise.all(asyncOperations)) as [Date | null];

        if (activeOsasqResendDate && (this.props.fields.osasq_resend_date || this.props.fields.osasq_resend_week_dif)) {
            await this.handleChangeCustomControl({ osasq_resend_date: null, osasq_resend_week_dif: null });
        }

        await this.setState({ isLoading: false, activeOsasqResendDate });
    }

    public componentWillUnmount() {
        this.abortController.abort();
    }

    public render() {
        return (
            <>
                {this.state.isLoading ? (
                    <Loader className="mt-3 mx-auto" />
                ) : (
                    <FormContextProvider
                        value={{
                            placeholderGroupName: 'diagnosticReport',
                            item: this.props.fields,
                            errors: this.props.errors,
                            onChange: async (key, value) => await this.handleChangeCustomControl({ [key]: value }),
                            onDoValidate: async (key) => await this.validateOneCustomControl(key),
                        }}
                    >
                        <DateField
                            className="mb-3"
                            error={this.props.errors.get('customControlData.fields.dateConsult')}
                            fieldKey="dateConsult"
                            hasErrors={this.props.errors.has('customControlData.fields.dateConsult')}
                            isRequired
                            label={translateKey('diagnosticReport.dateConsult')}
                            maxDate={DateTime.now().format()}
                            onChange={async (value) => {
                                await this.handleChangeCustomControl({ dateConsult: value });
                                await this.validateOneCustomControl('dateConsult');
                            }}
                            placeholder={`${translateKey('common.date')}...`}
                            size="medium"
                        />

                        <HeaderDivider text="Aanvullende specialistische bevindingen" />

                        <TextAreaField
                            error={this.props.errors.get('customControlData.fields.aanvullendeAnamnese')}
                            fieldKey="aanvullendeAnamnese"
                            hasErrors={this.props.errors.has('customControlData.fields.aanvullendeAnamnese')}
                            label="Aanvullende anamnese"
                            placeholder="Voer een aanvulling op de anamnese van de huisarts in..."
                        />

                        <TextAreaField
                            error={this.props.errors.get('customControlData.fields.aanvullendLichamelijkOnderzoek')}
                            fieldKey="aanvullendLichamelijkOnderzoek"
                            hasErrors={this.props.errors.has('customControlData.fields.aanvullendLichamelijkOnderzoek')}
                            label="Aanvullend lichamelijk onderzoek"
                            placeholder="Voer de resultaten van een aanvullend lichamelijk onderzoek in..."
                        />

                        <FormDivider />
                        <HeaderDivider text="Bevindingen slaaponderzoek" />

                        <TextField
                            automation="gemiddeldeAhi"
                            error={this.props.errors.get('customControlData.fields.gemiddeldeAhi')}
                            fieldKey="gemiddeldeAhi"
                            hasErrors={this.props.errors.has('customControlData.fields.gemiddeldeAhi')}
                            isRequired
                            label="Gemiddelde AHI"
                            placeholder=""
                            size="large"
                        />

                        <TextField
                            automation="gemiddeldeOdi"
                            error={this.props.errors.get('customControlData.fields.gemiddeldeOdi')}
                            fieldKey="gemiddeldeOdi"
                            hasErrors={this.props.errors.has('customControlData.fields.gemiddeldeOdi')}
                            isRequired
                            label="Gemiddelde ODI 3%"
                            placeholder=""
                            size="large"
                        />

                        <TextField
                            error={this.props.errors.get('customControlData.fields.odi4')}
                            fieldKey="odi4"
                            hasErrors={this.props.errors.has('customControlData.fields.odi4')}
                            isRequired
                            label="Gemiddelde ODI 4%"
                            placeholder=""
                            size="large"
                        />

                        <TextField
                            error={this.props.errors.get('customControlData.fields.gemiddeldeSaturatie')}
                            fieldKey="gemiddeldeSaturatie"
                            hasErrors={this.props.errors.has('customControlData.fields.gemiddeldeSaturatie')}
                            isRequired
                            label="Gemiddelde saturatie %"
                            placeholder=""
                            size="large"
                        />

                        <TextField
                            error={this.props.errors.get('customControlData.fields.laagstGemetenSaturatie')}
                            fieldKey="laagstGemetenSaturatie"
                            hasErrors={this.props.errors.has('customControlData.fields.laagstGemetenSaturatie')}
                            isRequired
                            label="Laagst gemeten saturatie %"
                            placeholder=""
                            size="large"
                        />

                        <RadioGroupField
                            automation="relevanteHypoxemie"
                            fieldKey="relevanteHypoxemie"
                            hasErrors={this.props.errors.has('customControlData.fields.relevanteHypoxemie')}
                            isRequired
                            label="Relevante Hypoxemie"
                            labels={[translateKey('common.yes'), translateKey('common.no')]}
                            onChange={async (value) => {
                                const update: Partial<IKsyosMedicalCenterCustomControlDataFields> = {};
                                update.relevanteHypoxemie = value === YesOrNo.yes;
                                if (value !== YesOrNo.yes) {
                                    update.relevanteHypoxemieRemarks = null;
                                }
                                await this.handleChangeCustomControl(update);
                            }}
                            options={[YesOrNo.yes, YesOrNo.no]}
                            value={
                                this.props.fields.relevanteHypoxemie === true
                                    ? YesOrNo.yes
                                    : this.props.fields.relevanteHypoxemie === false
                                      ? YesOrNo.no
                                      : null
                            }
                        />

                        {this.props.fields.relevanteHypoxemie && (
                            <TextAreaField
                                error={this.props.errors.get('customControlData.fields.relevanteHypoxemieRemarks')}
                                fieldKey="relevanteHypoxemieRemarks"
                                hasErrors={this.props.errors.has('customControlData.fields.relevanteHypoxemieRemarks')}
                                label={translateKey('common.remarks')}
                                placeholder={translateKey('common.remarksOrQuestions')}
                            />
                        )}

                        <RadioGroupField
                            automation="positieafhankelijk"
                            fieldKey="positieafhankelijk"
                            hasErrors={this.props.errors.has('customControlData.fields.positieafhankelijk')}
                            isRequired
                            label="Positieafhankelijk"
                            onChange={async (value) => {
                                const update: Partial<IKsyosMedicalCenterCustomControlDataFields> = {};
                                update.positieafhankelijk = value;
                                if (value !== 'Ja') {
                                    update.positieafhankelijkRemarks = null;
                                }
                                await this.handleChangeCustomControl(update);
                            }}
                            options={positieafhankelijkOptions}
                        />

                        {this.props.fields.positieafhankelijk === 'Ja' && (
                            <TextAreaField
                                error={this.props.errors.get('customControlData.fields.positieafhankelijkRemarks')}
                                fieldKey="positieafhankelijkRemarks"
                                hasErrors={this.props.errors.has('customControlData.fields.positieafhankelijkRemarks')}
                                label={translateKey('common.remarks')}
                                placeholder={translateKey('common.remarksOrQuestions')}
                            />
                        )}

                        <HorizontalFormGroup
                            hasErrors={
                                this.props.errors.has('customControlData.fields.veelBewegingen') ||
                                this.props.errors.has('customControlData.fields.slechteSlaapefficientie') ||
                                this.props.errors.has('customControlData.fields.afwijkendHartritme') ||
                                this.props.errors.has('customControlData.fields.afwijkendHartritmeRemarks') ||
                                this.props.errors.has(
                                    'customControlData.fields.overigeRelevanteBevindingenAndersText'
                                ) ||
                                this.props.errors.has('customControlData.fields.overigeRelevanteBevindingen')
                            }
                        >
                            <FormLabel>Overige relevante bevindingen</FormLabel>
                            <InputContainer>
                                <InputCheckbox
                                    automation="inefficienteSlaaphygiene"
                                    checked={this.props.fields.inefficienteSlaaphygiene}
                                    error={this.props.errors.get('customControlData.fields.inefficienteSlaaphygiene')}
                                    onChange={async (value) => {
                                        const update: Partial<IKsyosMedicalCenterCustomControlDataFields> = {};
                                        update.inefficienteSlaaphygiene = value;
                                        if (value !== true) {
                                            update.inefficienteSlaaphygieneRemarks = null;
                                        }
                                        await this.handleChangeCustomControl(update);
                                    }}
                                    onDoValidate={async () => {
                                        await this.validateOneCustomControl('inefficienteSlaaphygiene');
                                    }}
                                    text="Inefficiënte slaaphygiëne"
                                />
                                {this.props.fields.inefficienteSlaaphygiene && (
                                    <NullableInputText
                                        automation="inefficienteSlaaphygieneRemarks"
                                        error={this.props.errors.get(
                                            'customControlData.fields.inefficienteSlaaphygieneRemarks'
                                        )}
                                        onChange={async (value) =>
                                            await this.handleChangeCustomControl({
                                                inefficienteSlaaphygieneRemarks: value,
                                            })
                                        }
                                        onDoValidate={async () =>
                                            await this.validateOneCustomControl('inefficienteSlaaphygieneRemarks')
                                        }
                                        placeholder="Namelijk..."
                                        value={this.props.fields.inefficienteSlaaphygieneRemarks}
                                    />
                                )}
                                <InputCheckbox
                                    automation="veelBewegingen"
                                    checked={this.props.fields.veelBewegingen}
                                    error={this.props.errors.get('customControlData.fields.veelBewegingen')}
                                    onChange={async (value) =>
                                        await this.handleChangeCustomControl({ veelBewegingen: value })
                                    }
                                    onDoValidate={async () => {
                                        await this.validateOneCustomControl('veelBewegingen');
                                    }}
                                    text="Veel bewegingen"
                                />
                                <InputCheckbox
                                    automation="slechteSlaapefficientie"
                                    checked={this.props.fields.slechteSlaapefficientie}
                                    error={this.props.errors.get('customControlData.fields.slechteSlaapefficientie')}
                                    onChange={async (value) =>
                                        await this.handleChangeCustomControl({ slechteSlaapefficientie: value })
                                    }
                                    onDoValidate={async () => {
                                        await this.validateOneCustomControl('slechteSlaapefficientie');
                                    }}
                                    text="Niet optimale slaapefficiëntie"
                                />
                                <InputCheckbox
                                    automation="afwijkendHartritme"
                                    checked={this.props.fields.afwijkendHartritme}
                                    error={this.props.errors.get('customControlData.fields.afwijkendHartritme')}
                                    onChange={async (value) => {
                                        const update: Partial<IKsyosMedicalCenterCustomControlDataFields> = {};
                                        update.afwijkendHartritme = value;
                                        if (!value) {
                                            update.afwijkendHartritmeRemarks = null;
                                        } else {
                                            update.afwijkendHartritmeRemarks =
                                                'Bericht aan huisarts: Overweeg holteronderzoek voor deze patiënt';
                                        }
                                        await this.handleChangeCustomControl(update);
                                    }}
                                    onDoValidate={async () => await this.validateOneCustomControl('afwijkendHartritme')}
                                    text="Aanwijzing hartritmestoornis"
                                />
                                {this.props.fields.afwijkendHartritme && (
                                    <NullableInputText
                                        automation="afwijkendHartritmeRemarks"
                                        error={this.props.errors.get(
                                            'customControlData.fields.afwijkendHartritmeRemarks'
                                        )}
                                        onChange={async (value) =>
                                            await this.handleChangeCustomControl({ afwijkendHartritmeRemarks: value })
                                        }
                                        onDoValidate={async () =>
                                            await this.validateOneCustomControl('afwijkendHartritmeRemarks')
                                        }
                                        placeholder="Namelijk..."
                                        value={this.props.fields.afwijkendHartritmeRemarks}
                                    />
                                )}
                                <InputTextCheckbox
                                    automation="overigeRelevanteBevindingenAndersText"
                                    error={this.props.errors.get(
                                        'customControlData.fields.overigeRelevanteBevindingenAndersText'
                                    )}
                                    expandableTextArea={true}
                                    labelStyle={{ width: '100%' }}
                                    onChange={async (value, andersChecked) => {
                                        await this.handleChangeCustomControl({
                                            overigeRelevanteBevindingenAndersText: value,
                                        });
                                    }}
                                    onDoValidate={async () => {
                                        await this.validateOneCustomControl('overigeRelevanteBevindingenAndersText');
                                    }}
                                    placeholder="Anders, namelijk..."
                                    text={this.props.fields.overigeRelevanteBevindingenAndersText}
                                />
                            </InputContainer>
                        </HorizontalFormGroup>

                        <FormDivider />
                        <HeaderDivider text={translateKey('common.conclusion')} />

                        <FormGroup
                            hasErrors={this.props.errors.has('customControlData.fields.osa')}
                            isRequired
                            label="Obstructieve Slaapapneu (OSA)"
                        >
                            <RadioGroup
                                layout="row"
                                name="osa"
                                onChange={async (event) => {
                                    const update: Partial<IKsyosMedicalCenterCustomControlDataFields> = {
                                        osa: event.target.value === 'true',
                                        ...initializeOsaYesFlow(),
                                        behandeling1eLijnRemark: null,
                                    };
                                    await this.handleChangeCustomControl(update);
                                }}
                                value={this.props.fields.osa?.toString() ?? ''}
                            >
                                <RadioButton
                                    label="Ja"
                                    value="true"
                                />
                                <RadioButton
                                    label="Nee"
                                    value="false"
                                />
                            </RadioGroup>
                        </FormGroup>

                        {this.props.fields.osa === true && this.showOsaYesFlow()}
                        {this.props.fields.osa === false && this.showOsaNoFlow()}
                    </FormContextProvider>
                )}
            </>
        );
    }

    private showOsaYesFlow() {
        return (
            <>
                <RadioGroupField
                    automation="mateVanOsa"
                    error={this.props.errors.get('customControlData.fields.mateVanOsa')}
                    fieldKey="mateVanOsa"
                    hasErrors={this.props.errors.has('customControlData.fields.mateVanOsa')}
                    isRequired
                    label="Mate van OSA"
                    labels={mateVanOsaOptions}
                    options={mateVanOsaOptions}
                    radioButtonsStacked
                />

                <RadioGroupField
                    automation="mateVanZiektelast"
                    error={this.props.errors.get('customControlData.fields.mateVanZiektelast')}
                    fieldKey="mateVanZiektelast"
                    hasErrors={this.props.errors.has('customControlData.fields.mateVanZiektelast')}
                    isRequired
                    label="Mate van ziektelast"
                    labels={mateVanZiektelastOptions}
                    options={mateVanZiektelastOptions}
                    radioButtonsStacked
                />

                <HorizontalFormGroupRead>
                    <FormLabel>BMI categorie</FormLabel>
                    <InputContainer>
                        <FormParagraph>
                            {this.props.fields.bmiCategorie
                                ? this.props.fields.bmiCategorie
                                : translateKey('common.unknown')}
                        </FormParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>

                <HorizontalFormGroupRead>
                    <FormLabel>Comorbiditeiten volgens verwijzing</FormLabel>
                    <InputContainer>
                        {this.props.fields.comorbiditeitenVolgensHuisarts.hoge_bloeddruk && (
                            <FormInlineGroup value="Hoge bloeddruk" />
                        )}
                        {this.props.fields.comorbiditeitenVolgensHuisarts.cardiale_problematiek && (
                            <FormInlineGroup value="Cardiale problematiek" />
                        )}
                        {this.props.fields.comorbiditeitenVolgensHuisarts.diabetes_mellitus && (
                            <FormInlineGroup value="Diabetes mellitus" />
                        )}
                        {this.props.fields.comorbiditeitenVolgensHuisarts.burn_out_of_depressieve_klachten && (
                            <FormInlineGroup value="Burn-out of depressieve klachten" />
                        )}
                        {this.props.fields.comorbiditeitenVolgensHuisarts.anders_text && (
                            <FormInlineGroup value={this.props.fields.comorbiditeitenVolgensHuisarts.anders_text} />
                        )}
                    </InputContainer>
                </HorizontalFormGroupRead>

                <HorizontalFormGroup
                    hasErrors={
                        this.props.errors.has('customControlData.fields.andereComorbiditeiten') ||
                        this.props.errors.has('customControlData.fields.andereComorbiditeitenRemarks')
                    }
                >
                    <FormLabel isRequired>Aanvullende comorbiditeiten</FormLabel>
                    <InputContainer>
                        <RadioGroupInput
                            automation="andereComorbiditeiten"
                            error={this.props.errors.get('customControlData.fields.andereComorbiditeiten')}
                            fieldKey="andereComorbiditeiten"
                            labels={[translateKey('common.yes'), translateKey('common.no')]}
                            onChange={async (value) => {
                                const update: Partial<IKsyosMedicalCenterCustomControlDataFields> = {};
                                update.andereComorbiditeiten = value === YesOrNo.yes;
                                if (value !== YesOrNo.yes) {
                                    update.andereComorbiditeitenRemarks = null;
                                }
                                await this.handleChangeCustomControl(update);
                            }}
                            onDoValidate={async () => {
                                await this.validateOneCustomControl('andereComorbiditeiten');
                            }}
                            options={[YesOrNo.yes, YesOrNo.no]}
                            value={
                                this.props.fields.andereComorbiditeiten === true
                                    ? YesOrNo.yes
                                    : this.props.fields.andereComorbiditeiten === false
                                      ? YesOrNo.no
                                      : null
                            }
                        />

                        {this.props.fields.andereComorbiditeiten && (
                            <>
                                <NullableInputText
                                    error={this.props.errors.get(
                                        'customControlData.fields.andereComorbiditeitenRemarks'
                                    )}
                                    fieldKey="andereComorbiditeitenRemarks"
                                    onChange={async (value) =>
                                        await this.handleChangeCustomControl({ andereComorbiditeitenRemarks: value })
                                    }
                                    onDoValidate={async () => {
                                        await this.validateOneCustomControl('andereComorbiditeitenRemarks');
                                    }}
                                    placeholder="Namelijk..."
                                />
                            </>
                        )}
                    </InputContainer>
                </HorizontalFormGroup>

                <FormDivider />
                <HeaderDivider text={translateKey('diagnosticReport.policy')} />

                <FormGroup
                    hasErrors={this.hasPolicyErrors()}
                    isRequired
                    label={translateKey('diagnosticReport.policy')}
                >
                    <InputContainer>
                        <InputCheckbox
                            automation="spt"
                            checked={this.props.fields.beleid.spt}
                            error={this.props.errors.get('customControlData.fields.beleid.spt')}
                            onChange={async (value) => await this.handleChangeBeleid('spt', value)}
                            onDoValidate={async () => await this.validateOneCustomControl('beleid')}
                            text="SPT"
                        />
                        {this.props.fields.beleid.spt && (
                            <RadioGroupWithTextArea
                                error={
                                    this.props.errors.get('customControlData.fields.beleid.sptOption') ??
                                    this.props.errors.get('customControlData.fields.beleid.sptOptionRemark')
                                }
                                name="sptOption"
                                onChange={async (value) => {
                                    await this.handleChangeBeleid('sptOption', value as unknown as SptOption);
                                }}
                                onChangeTextArea={async (value) =>
                                    await this.handleChangeBeleid('sptOptionRemark', value)
                                }
                                radioItems={this.enumToIOption(SptOption)}
                                textAreaValue={this.props.fields.beleid.sptOptionRemark ?? ''}
                                value={this.props.fields.beleid.sptOption ?? ''}
                            />
                        )}
                        <InputCheckbox
                            automation="mra"
                            checked={this.props.fields.beleid.mra}
                            error={this.props.errors.get('customControlData.fields.beleid.mra')}
                            onChange={async (value) => await this.handleChangeBeleid('mra', value)}
                            onDoValidate={async () => await this.validateOneCustomControl('beleid')}
                            text="MRA"
                        />
                        {this.props.fields.beleid.mra && (
                            <RadioGroupWithTextArea
                                error={
                                    this.props.errors.get('customControlData.fields.beleid.mraAbcdScore') ??
                                    this.props.errors.get('customControlData.fields.beleid.mraAbcdScoreRemark')
                                }
                                name="mraAbcdScore"
                                onChange={async (value) => {
                                    await this.handleChangeBeleid('mraAbcdScore', value as unknown as MraAbcdScore);
                                }}
                                onChangeTextArea={async (value) =>
                                    await this.handleChangeBeleid('mraAbcdScoreRemark', value)
                                }
                                radioItems={this.enumToIOption(MraAbcdScore)}
                                textAreaValue={this.props.fields.beleid.mraAbcdScoreRemark ?? ''}
                                value={this.props.fields.beleid.mraAbcdScore ?? ''}
                            />
                        )}
                        <InputCheckbox
                            automation="cpap"
                            checked={this.props.fields.beleid.cpap}
                            error={this.props.errors.get('customControlData.fields.beleid.cpap')}
                            onChange={async (value) => await this.handleChangeBeleid('cpap', value)}
                            onDoValidate={async () => await this.validateOneCustomControl('beleid')}
                            text="CPAP"
                        />

                        {this.props.fields.beleid.cpap && (
                            <RadioGroupWithTextArea
                                error={
                                    this.props.errors.get('customControlData.fields.beleid.cpapBeleidOption') ??
                                    this.props.errors.get('customControlData.fields.beleid.cpapBeleidOptionRemark')
                                }
                                name="cpapBeleidOption"
                                onChange={async (event) => {
                                    await this.handleChangeBeleid(
                                        'cpapBeleidOption',
                                        event as unknown as CpapBeleidOption
                                    );
                                }}
                                onChangeTextArea={async (value) =>
                                    await this.handleChangeBeleid('cpapBeleidOptionRemark', value)
                                }
                                radioItems={this.enumToIOption(CpapBeleidOption)}
                                textAreaValue={this.props.fields.beleid.cpapBeleidOptionRemark ?? ''}
                                value={this.props.fields.beleid.cpapBeleidOption ?? ''}
                            />
                        )}

                        <InputCheckbox
                            checked={this.props.fields.beleid.adviesLifestyleVerandering}
                            error={this.props.errors.get('customControlData.fields.beleid.adviesLifestyleVerandering')}
                            onChange={async (value) => {
                                const update: IBeleid = { ...this.props.fields.beleid };
                                update.adviesLifestyleVerandering = value;
                                if (!value) {
                                    update.adviesLifestyleVeranderingRemarks = null;
                                }
                                await this.handleChangeCustomControl({ beleid: { ...update } });
                            }}
                            onDoValidate={async () => await this.validateOneCustomControl('beleid')}
                            text="Advies lifestyle verandering"
                        />

                        {this.props.fields.beleid.adviesLifestyleVerandering && (
                            <NullableInputText
                                automation="adviesLifestyleVeranderingRemarks"
                                error={this.props.errors.get(
                                    'customControlData.fields.beleid.adviesLifestyleVeranderingRemarks'
                                )}
                                onChange={async (value) =>
                                    await this.handleChangeBeleid('adviesLifestyleVeranderingRemarks', value)
                                }
                                onDoValidate={async () => await this.validateOneCustomControl('beleid')}
                                placeholder="Namelijk..."
                                value={this.props.fields.beleid.adviesLifestyleVeranderingRemarks}
                            />
                        )}

                        <InputCheckbox
                            checked={this.props.fields.beleid.doorverwijzen}
                            error={this.props.errors.get('customControlData.fields.doorverwijzen')}
                            onChange={async (value) => {
                                const update: IBeleid = { ...this.props.fields.beleid };
                                update.doorverwijzen = value;
                                if (!value) {
                                    update.doorverwijzenRemarks = null;
                                }
                                await this.handleChangeCustomControl({ beleid: { ...update } });
                            }}
                            onDoValidate={async () => await this.validateOneCustomControl('beleid')}
                            text="Doorverwijzen"
                        />

                        {this.props.fields.beleid.doorverwijzen && (
                            <NullableInputText
                                automation="doorverwijzenRemarks"
                                error={this.props.errors.get('customControlData.fields.doorverwijzenRemarks')}
                                onChange={async (value) => await this.handleChangeBeleid('doorverwijzenRemarks', value)}
                                onDoValidate={async () => await this.validateOneCustomControl('beleid')}
                                placeholder="Vanwege..."
                                value={this.props.fields.beleid.doorverwijzenRemarks}
                            />
                        )}

                        <InputTextCheckbox
                            automation="beleidAndersText"
                            error={this.props.errors.get('customControlData.fields.beleidAndersText')}
                            expandableTextArea={true}
                            labelStyle={{ width: '100%' }}
                            onChange={async (value) => {
                                await this.handleChangeBeleid('beleidAndersText', value);
                            }}
                            onDoValidate={async () => await this.validateOneCustomControl('beleid')}
                            placeholder="Anders, namelijk..."
                            text={this.props.fields.beleid.beleidAndersText}
                        />
                    </InputContainer>
                </FormGroup>

                {this.props.fields.beleid.cpap && this.renderCpapSettings()}

                {this.showToesturenButtonAndModal()}

                {this.showPatientEmailInformation()}

                <HorizontalFormGroup hasErrors={this.props.errors.has('customControlData.fields.volgensProtocol')}>
                    <FormLabel>Vervolg afspraak met specialist</FormLabel>
                    <InputContainer>
                        <InputCheckbox
                            automation="volgensProtocol"
                            checked={this.props.fields.volgensProtocol}
                            error={this.props.errors.get('customControlData.fields.volgensProtocol')}
                            onChange={async (value) => await this.handleChangeCustomControl({ volgensProtocol: value })}
                            onDoValidate={async () => await this.validateOneCustomControl('volgensProtocol')}
                            text="Volgens protocol"
                        />
                        <InputTextCheckbox
                            automation="vervolgAfspraakMetSpecialistAndersText"
                            error={this.props.errors.get(
                                'customControlData.fields.vervolgAfspraakMetSpecialistAndersText'
                            )}
                            expandableTextArea={true}
                            labelStyle={{ width: '100%' }}
                            onChange={async (value) => {
                                await this.handleChangeCustomControl({
                                    vervolgAfspraakMetSpecialistAndersText: value,
                                });
                            }}
                            onDoValidate={async () =>
                                await this.validateOneCustomControl('vervolgAfspraakMetSpecialistAndersText')
                            }
                            placeholder="Anders, namelijk..."
                            text={this.props.fields.vervolgAfspraakMetSpecialistAndersText}
                        />
                    </InputContainer>
                </HorizontalFormGroup>

                <HorizontalFormGroup
                    hasErrors={this.props.errors.has('customControlData.fields.osasq_resend_week_dif')}
                >
                    <FormLabel>Herzenden vragenlijsten</FormLabel>
                    <InputContainer size="auto">
                        <CheckboxInput
                            automation="osasqResendDateCheckbox"
                            checked={!!this.props.fields.osasq_resend_week_dif}
                            disabled={!!this.state.activeOsasqResendDate}
                            error={this.props.errors.get('customControlData.fields.osasq_resend_date')}
                            onChange={async (value) => {
                                await this.handleChangeCustomControl({
                                    osasq_resend_week_dif: value ? 6 : null,
                                    osasq_resend_date: value
                                        ? DateTime.now().add(6, 'weeks').format('YYYY-MM-DD')
                                        : null,
                                });
                            }}
                            onDoValidate={async () => {
                                await this.validateOneCustomControl('osasq_resend_week_dif');
                            }}
                            text="Ja, na"
                        />
                    </InputContainer>
                    <InputContainer className="col-sm-12 col-xl-6">
                        <NumberInput
                            disabled={!!this.state.activeOsasqResendDate}
                            error={this.props.errors.get('customControlData.fields.osasq_resend_week_dif')}
                            fieldKey="osasq_resend_week_dif"
                            onChange={async (value) => {
                                await this.handleChangeCustomControl({
                                    osasq_resend_week_dif: value,
                                    osasq_resend_date: value
                                        ? DateTime.now().add(value, 'weeks').format('YYYY-MM-DD')
                                        : null,
                                });
                            }}
                            onDoValidate={async () => {
                                await this.validateOneCustomControl('osasq_resend_week_dif');
                            }}
                            placeholder=""
                            text={`${this.props.fields.osasq_resend_week_dif === 1 ? 'week' : 'weken'} ${
                                this.props.fields.osasq_resend_week_dif
                                    ? `(${DateTime.now()
                                          .add(this.props.fields.osasq_resend_week_dif, 'weeks')
                                          .format('D MMMM YYYY')})`
                                    : ''
                            }`}
                            textStyle={this.state.activeOsasqResendDate ? { color: '#A69A91' } : undefined}
                            value={this.props.fields.osasq_resend_week_dif}
                            width="small"
                        />
                    </InputContainer>
                    {this.state.activeOsasqResendDate && (
                        <Alert appearance="warning">
                            Er staan al vragenlijsten ingepland voor deze patiënt (
                            {DateTime.from(this.state.activeOsasqResendDate).format('DD-MM-YYYY')}), als je toch een
                            ander moment in wilt plannen kan dit in het zorgpad van de patiënt.
                        </Alert>
                    )}
                </HorizontalFormGroup>

                <TextAreaField
                    automation="adviesAanDeHuisarts"
                    error={this.props.errors.get('customControlData.fields.adviesAanDeHuisarts')}
                    fieldKey="adviesAanDeHuisarts"
                    hasErrors={this.props.errors.has('customControlData.fields.adviesAanDeHuisarts')}
                    label="Advies aan de huisarts"
                    placeholder="Het advies..."
                />

                <HorizontalFormGroup hasErrors={this.props.errors.has('remark')}>
                    <FormLabel>{translateKey('common.remarks')}</FormLabel>
                    <InputContainer>
                        <InputTextArea
                            automation="remark"
                            error={this.props.errors.get('remark')}
                            onChange={async (value) => await this.props.handleChange('remark', value)}
                            onDoValidate={async () => await this.props.validateOne('remark')}
                            placeholder={translateKey('common.remarksOrQuestions')}
                            value={this.props.remark}
                        />
                    </InputContainer>
                </HorizontalFormGroup>

                <RadioGroupField
                    automation="rijbevoegdheidBesproken"
                    error={this.props.errors.get('customControlData.fields.rijbevoegdheidBesproken')}
                    fieldKey="rijbevoegdheidBesproken"
                    hasErrors={this.props.errors.has('customControlData.fields.rijbevoegdheidBesproken')}
                    isRequired
                    label="Rijbevoegdheid besproken"
                    onChange={async (value) => {
                        const update: Partial<IKsyosMedicalCenterCustomControlDataFields> = {};
                        update.rijbevoegdheidBesproken = value;
                        await this.handleChangeCustomControl(update);
                    }}
                    options={rijbevoegdheidBesprokenOptions}
                />

                <TextAreaField
                    automation="rijbevoegdheidBesprokenRemark"
                    error={this.props.errors.get('customControlData.fields.rijbevoegdheidBesprokenRemark')}
                    fieldKey="rijbevoegdheidBesprokenRemark"
                    hasErrors={this.props.errors.has('customControlData.fields.rijbevoegdheidBesprokenRemark')}
                    label="Toelichting rijbevoegdheid"
                    placeholder="Een toelichting of opmerkingen..."
                />
            </>
        );
    }

    private showOsaNoFlow() {
        const adviesAanDeHuisartsOptionElements: (JSX.Element | null)[] = [
            null,
            <TextAreaField
                error={this.props.errors.get('behandeling1eLijnRemark')}
                fieldKey="behandeling1eLijnRemark"
                key={'behandeling1eLijnRemark'}
                label=""
                placeholder="Een toelichting, opmerkingen, of vragen…"
            />,
            null,
            null,
            null,
        ];

        return (
            <>
                <HorizontalFormGroupRead>
                    <FormLabel>BMI categorie</FormLabel>
                    <InputContainer>
                        <FormParagraph>
                            {this.props.fields.bmiCategorie
                                ? this.props.fields.bmiCategorie
                                : translateKey('common.unknown')}
                        </FormParagraph>
                    </InputContainer>
                </HorizontalFormGroupRead>

                <HorizontalFormGroupRead>
                    <FormLabel>Comorbiditeiten volgens verwijzing</FormLabel>
                    <InputContainer>
                        {this.props.fields.comorbiditeitenVolgensHuisarts.hoge_bloeddruk && (
                            <FormInlineGroup value="Hoge bloeddruk" />
                        )}
                        {this.props.fields.comorbiditeitenVolgensHuisarts.cardiale_problematiek && (
                            <FormInlineGroup value="Cardiale problematiek" />
                        )}
                        {this.props.fields.comorbiditeitenVolgensHuisarts.diabetes_mellitus && (
                            <FormInlineGroup value="Diabetes mellitus" />
                        )}
                        {this.props.fields.comorbiditeitenVolgensHuisarts.burn_out_of_depressieve_klachten && (
                            <FormInlineGroup value="Burn-out of depressieve klachten" />
                        )}
                        {this.props.fields.comorbiditeitenVolgensHuisarts.anders_text && (
                            <FormInlineGroup value={this.props.fields.comorbiditeitenVolgensHuisarts.anders_text} />
                        )}
                    </InputContainer>
                </HorizontalFormGroupRead>

                <FormDivider />
                <HeaderDivider text={translateKey('diagnosticReport.policy')} />

                <HorizontalFormGroup hasErrors={this.props.errors.has('customControlData.fields.adviesAanDeHuisarts')}>
                    <FormLabel isRequired>Advies aan de huisarts</FormLabel>
                    <InputContainer>
                        <RadioElementGroup
                            customOption={
                                <InputRadio
                                    label={
                                        <NullableInputText
                                            automation="adviesAanDeHuisarts"
                                            onChange={async (value) =>
                                                await this.handleChangeCustomControl({ adviesAanDeHuisarts: value })
                                            }
                                            onDoValidate={async () =>
                                                await this.validateOneCustomControl('adviesAanDeHuisarts')
                                            }
                                            placeholder="Anders, namelijk..."
                                            value={
                                                this.props.fields.adviesAanDeHuisarts &&
                                                adviesAanDeHuisartsOptions.indexOf(
                                                    this.props.fields.adviesAanDeHuisarts
                                                ) === -1
                                                    ? this.props.fields.adviesAanDeHuisarts
                                                    : null
                                            }
                                        />
                                    }
                                    labelStyle={{ width: '90%' }}
                                    name={'adviesAanDeHuisarts'}
                                    onChange={async (value) =>
                                        await this.handleChangeCustomControl({
                                            adviesAanDeHuisarts: value ? value : '',
                                            behandeling1eLijnRemark: null,
                                        })
                                    }
                                    onValue={''}
                                    value={
                                        this.props.fields.adviesAanDeHuisarts === null ||
                                        adviesAanDeHuisartsOptions.indexOf(this.props.fields.adviesAanDeHuisarts) !== -1
                                            ? null
                                            : ''
                                    }
                                />
                            }
                            error={this.props.errors.get('customControlData.fields.adviesAanDeHuisarts')}
                            fieldKey="adviesAanDeHuisarts"
                            labels={adviesAanDeHuisartsOptions}
                            onChange={async (value) =>
                                await this.handleChangeCustomControl({
                                    adviesAanDeHuisarts: value,
                                    behandeling1eLijnRemark: null,
                                })
                            }
                            optionElements={adviesAanDeHuisartsOptionElements}
                            options={adviesAanDeHuisartsOptions}
                        />
                    </InputContainer>
                </HorizontalFormGroup>

                {this.showToesturenButtonAndModal()}

                {this.showPatientEmailInformation()}

                <HorizontalFormGroup hasErrors={this.props.errors.has('remark')}>
                    <FormLabel>{translateKey('common.remarks')}</FormLabel>
                    <InputContainer>
                        <InputTextArea
                            automation="remark"
                            error={this.props.errors.get('remark')}
                            onChange={async (value) => await this.props.handleChange('remark', value)}
                            onDoValidate={async () => await this.props.validateOne('remark')}
                            placeholder={translateKey('common.remarksOrQuestions')}
                            value={this.props.remark}
                        />
                    </InputContainer>
                </HorizontalFormGroup>
            </>
        );
    }

    private handleChangeOsasqSendInfo(beleid: IBeleid, osa: boolean | null) {
        let weekDif;

        if (!this.state.activeOsasqResendDate && osa) {
            if (beleid.mra) {
                weekDif = 12;
            } else if (beleid.cpap || beleid.spt) {
                weekDif = 6;
            }
        }

        return weekDif;
    }

    private showToesturenButtonAndModal() {
        return (
            <>
                {this.state.therapielandProgrammaIsModalShown && this.props.fields.checkedTreatments && (
                    <TherapielandProgrammasModal
                        checkedTreatments={this.props.fields.checkedTreatments}
                        handleChange={async (values: ICheckedTreatment[]) => {
                            await this.setState({ therapielandProgrammaIsModalShown: false });
                            await this.handleChangeCustomControl({ checkedTreatments: values });
                        }}
                        handleClose={async () => {
                            await this.setState({ therapielandProgrammaIsModalShown: false });
                        }}
                        isOpen={this.state.therapielandProgrammaIsModalShown}
                        patientId={this.props.request.patientId}
                    />
                )}

                <HorizontalFormGroup>
                    <FormLabel>E-health programma´s</FormLabel>
                    <InputContainer>
                        {this.props.fields.checkedTreatments?.map((treatment: ICheckedTreatment) => {
                            return (
                                <FormParagraph
                                    bold
                                    key={treatment.id}
                                >
                                    {treatment.treatmentName + ' '}
                                    <IconOnClick
                                        icon={'delete'}
                                        onClick={async () => {
                                            const updatedTreatments = this.props.fields.checkedTreatments?.filter(
                                                (checkedTreatment: ICheckedTreatment) =>
                                                    checkedTreatment.id !== treatment.id
                                            );
                                            await this.handleChangeCustomControl({
                                                checkedTreatments: updatedTreatments,
                                            });
                                        }}
                                    />
                                </FormParagraph>
                            );
                        })}

                        <ButtonContainer
                            buttons={[
                                <CheckMarkButton
                                    disabled={!this.state.hasActiveGgzbTherServiceConsumerRights}
                                    isChecked={false}
                                    key={'selectTherapielandProgramme'}
                                    onClick={() => this.setState({ therapielandProgrammaIsModalShown: true })}
                                >
                                    {translateKey('diagnosticReport.selectTherapyLandProgramme')}
                                </CheckMarkButton>,
                            ]}
                            noMargin
                            wrapContent
                        />
                    </InputContainer>
                </HorizontalFormGroup>
            </>
        );
    }

    private showPatientEmailInformation() {
        return (
            <>
                {!this.state.hasActiveGgzbTherServiceConsumerRights && (
                    <Alert appearance="warning">
                        Je hebt geen rechten voor het aanvragen van de programma´s. Neem contact op met de servicedesk.
                    </Alert>
                )}

                {!!this.props.fields.checkedTreatments?.length && (
                    <Alert appearance="warning">Controleer goed. Klopt het e-mailadres van de patiënt?</Alert>
                )}

                {!!this.props.fields.checkedTreatments?.length && (
                    <HorizontalFormGroup
                        hasErrors={
                            this.props.errors.has('customControlData.fields.emailAddress') ||
                            this.state.emailAddressErrorShown
                        }
                    >
                        <FormLabel isRequired>{translateKey('common.emailAddress')}</FormLabel>
                        <InputContainer>
                            <TextInput
                                error={this.showEmailError(
                                    this.state.emailAddressErrorShown,
                                    this.props.errors.get('customControlData.fields.emailAddress')
                                )}
                                onBlur={async () => {
                                    await this.validateOneCustomControl('emailAddress');
                                }}
                                onChange={async ({ target: { value } }) => {
                                    await this.setState({ emailAddressErrorShown: false });
                                    await this.handleChangeCustomControl({ emailAddress: value });
                                    if (
                                        await isEmailDuplicateForPatient(
                                            this.props.fields.emailAddress,
                                            this.props.request.patientId,
                                            this.abortController.signal
                                        )
                                    ) {
                                        await this.setState({ emailAddressErrorShown: true });
                                    }
                                    await this.props.setButtonDisabled(
                                        this.state.emailAddressErrorShown ||
                                            !this.state.hasActiveGgzbTherServiceConsumerRights
                                    );
                                }}
                                placeholder={translateKey('diagnosticReport.patientEmailAddress')}
                                value={this.props.fields.emailAddress || ''}
                            />
                        </InputContainer>
                    </HorizontalFormGroup>
                )}
            </>
        );
    }

    private renderCpapSettings() {
        return (
            <FormGroup
                hasErrors={
                    this.props.errors.has('customControlData.fields.beleid.cpapOption') ||
                    this.props.errors.has('customControlData.fields.beleid.cpapOptionRemark')
                }
                isRequired={true}
                label={'CPAP instellingen'}
            >
                <RadioGroup
                    error={this.props.errors.get('customControlData.fields.beleid.cpapOption')}
                    name="cpapOption"
                    onChange={async (e) => {
                        await this.handleChangeBeleid('cpapOption', e.target.value);
                    }}
                    value={this.props.fields.beleid.cpapOption ?? ''}
                >
                    <Radio
                        label={CpapOption.standard}
                        value={CpapOption.standard}
                    />
                    <Radio
                        label={CpapOption.custom}
                        value={CpapOption.custom}
                    />
                    {this.props.fields.beleid.cpapOption === CpapOption.custom ? (
                        <TextArea
                            className={'automation-cpapOptionRemark'}
                            error={this.props.errors.get('customControlData.fields.beleid.cpapOptionRemark')}
                            onChange={async (e) => await this.handleChangeBeleid('cpapOptionRemark', e.target.value)}
                            placeholder="Namelijk..."
                            value={this.props.fields.beleid.cpapOptionRemark ?? ''}
                            variant="outline"
                        />
                    ) : (
                        <></>
                    )}
                </RadioGroup>
            </FormGroup>
        );
    }
    private async handleChangeBeleid<K extends keyof IBeleid>(name: K, value: IBeleid[K]) {
        const customControlDataUpdate: Partial<IKsyosMedicalCenterCustomControlDataFields> = {
            beleid: {
                ...this.props.fields.beleid,
                [name]: value,
            },
            osasq_resend_week_dif: this.props.fields.osasq_resend_week_dif,
            osasq_resend_date: this.props.fields.osasq_resend_date,
        };

        if (customControlDataUpdate.beleid) {
            if (name === 'cpap') {
                if (value) {
                    customControlDataUpdate.beleid.cpapOption = CpapOption.standard;
                } else {
                    customControlDataUpdate.beleid.cpapBeleidOption = null;
                    customControlDataUpdate.beleid.cpapBeleidOptionRemark = null;
                    customControlDataUpdate.beleid.cpapOption = null;
                    customControlDataUpdate.beleid.cpapOptionRemark = null;
                }
            }

            if (name === 'cpapBeleidOption') {
                customControlDataUpdate.beleid.cpapBeleidOptionRemark = cpapBeleidOptionsDefaultRemarks.get(
                    value as CpapBeleidOption
                )!;
            }

            if (name === 'cpapOption' && value !== CpapOption.custom) {
                customControlDataUpdate.beleid.cpapOptionRemark = null;
            }

            if (name === 'mra') {
                customControlDataUpdate.beleid.mraAbcdScore = null;
                customControlDataUpdate.beleid.mraAbcdScoreRemark = null;
            }

            if (name === 'mraAbcdScore') {
                customControlDataUpdate.beleid.mraAbcdScoreRemark = mraAbcdScoresDefaultRemarks.get(
                    value as MraAbcdScore
                )!;
            }

            if (name === 'spt') {
                customControlDataUpdate.beleid.sptOptionRemark = null;
                customControlDataUpdate.beleid.sptOption = null;
            }

            if (name === 'sptOption') {
                customControlDataUpdate.beleid.sptOptionRemark = sptOptionsDefaultRemarks.get(value as SptOption)!;
            }

            const weekDif = this.handleChangeOsasqSendInfo(customControlDataUpdate.beleid, this.props.fields.osa);

            if (weekDif) {
                customControlDataUpdate.osasq_resend_week_dif = weekDif;
                customControlDataUpdate.osasq_resend_date = DateTime.now().add(weekDif, 'weeks').format('YYYY-MM-DD');
            } else {
                customControlDataUpdate.osasq_resend_week_dif = null;
                customControlDataUpdate.osasq_resend_date = null;
            }
        }

        await this.handleChangeCustomControl(customControlDataUpdate);
    }

    private async handleChangeCustomControl(change: Partial<IKsyosMedicalCenterCustomControlDataFields>) {
        const customControlDataUpdate = {
            fields: {
                ...this.props.fields,
                ...change,
            },
        };
        await this.props.handleChangeCustomControl(customControlDataUpdate);
    }

    private async validateOneCustomControl(name: keyof IKsyosMedicalCenterCustomControlDataFields) {
        await this.props.validateOneCustomControl(String('fields.' + name));
    }

    private async loadReferralCustomControlData(referralRequestId: string) {
        const url = `/api/requests/${referralRequestId}`;
        const result = await fetchJson(url, 'GET');
        const customControlData = result.json.customControlData;

        if (customControlData) {
            await this.handleChangeCustomControl({
                bmiCategorie: getBmiCategorie(
                    Number(
                        calculateBmi(customControlData.consult.gewicht, customControlData.consult.lengte).replace(
                            ',',
                            '.'
                        )
                    )
                ),
                comorbiditeitenVolgensHuisarts: {
                    ...initializeComorbiditeitenVolgensHuisarts(),
                    ...customControlData.consult.comorbiditeiten,
                },
                typeSlaaprecorder: customControlData.typeSlaaprecorder ?? SleepRecorderType.nightowl,
            });
        }
    }

    private async loadEmailPatient() {
        const result = await fetchJson(`/api/patients/${this.props.request.patientId}`, 'GET');
        await this.handleChangeCustomControl({ emailAddress: result.json.emailAddress });
    }

    private async getDataFromOsasPdfReports() {
        const hasToRetrieveData = osasc2PdfKeys.some((key) => !this.props.fields[key]);

        if (hasToRetrieveData) {
            const result = await fetchJson(
                `/api/requests/${this.props.request.customControlData.referralRequestId}/getDataFromOsasPdfReports`,
                'GET'
            );

            if (result.json.data) {
                const resultingObject: any = {};
                osasc2PdfKeys.forEach(
                    (key) => (resultingObject[key] = this.props.fields[key] || result.json.data[key])
                );
                await this.handleChangeCustomControl(resultingObject);
            }
        }
    }

    private showEmailError(isDuplicateEmail: boolean, validationEmailError?: string) {
        if (validationEmailError) {
            return getValidationErrorTranslation(validationEmailError);
        } else if (isDuplicateEmail) {
            return getValidationErrorTranslation('duplicatedEmailTherapieland');
        }

        return undefined;
    }

    private async checkHasActiveGgzbTherServiceConsumerRights() {
        const hasRights = await hasActiveGgzbTherServiceConsumerRights(
            this.props.selectedEmploymentId,
            this.abortController.signal
        );

        await this.setState({ hasActiveGgzbTherServiceConsumerRights: hasRights });
        await this.props.setButtonDisabled(!hasRights);
    }

    private hasPolicyErrors() {
        return (
            this.props.errors.has('customControlData.fields.beleid.spt') ||
            this.props.errors.has('customControlData.fields.beleid.sptOption') ||
            this.props.errors.has('customControlData.fields.beleid.sptOptionRemark') ||
            this.props.errors.has('customControlData.fields.beleid.mra') ||
            this.props.errors.has('customControlData.fields.beleid.mraAbcdScore') ||
            this.props.errors.has('customControlData.fields.beleid.mraAbcdScoreRemark') ||
            this.props.errors.has('customControlData.fields.beleid.cpap') ||
            this.props.errors.has('customControlData.fields.beleid.cpapBeleidOption') ||
            this.props.errors.has('customControlData.fields.beleid.cpapBeleidOptionRemark') ||
            this.props.errors.has('customControlData.fields.beleid.adviesLifestyleVerandering') ||
            this.props.errors.has('customControlData.fields.beleid.adviesLifestyleVeranderingRemarks') ||
            this.props.errors.has('customControlData.fields.doorverwijzen') ||
            this.props.errors.has('customControlData.fields.doorverwijzenRemarks') ||
            this.props.errors.has('customControlData.fields.beleidAndersText')
        );
    }

    private enumToIOption<T extends Record<string, string>>(enumObject: T) {
        return Object.keys(enumObject).map((key) => {
            return { value: enumObject[key], label: enumObject[key] };
        });
    }
}

function initializeBeleid() {
    return {
        spt: false,
        sptOption: null,
        sptOptionRemark: null,
        mra: false,
        mraAbcdScore: null,
        mraAbcdScoreRemark: null,
        cpap: false,
        cpapOption: null,
        cpapOptionRemark: null,
        cpapBeleidOption: null,
        cpapBeleidOptionRemark: null,
        adviesLifestyleVerandering: false,
        adviesLifestyleVeranderingRemarks: null,
        doorverwijzen: false,
        doorverwijzenRemarks: null,
        beleidAndersText: null,
    };
}

function initializeOsaYesFlow() {
    return {
        mateVanOsa: null,
        mateVanZiektelast: null,
        andereComorbiditeiten: null,
        andereComorbiditeitenRemarks: null,
        beleid: initializeBeleid(),
        volgensProtocol: false,
        vervolgAfspraakMetSpecialistAndersText: null,
        rijbevoegdheidBesproken: null,
        rijbevoegdheidBesprokenRemark: null,
        osasq_resend_date: null,
        osasq_resend_week_dif: null,
        adviesAanDeHuisarts: null,
        checkedTreatments: [],
    };
}

function initializeComorbiditeitenVolgensHuisarts() {
    return {
        burn_out_of_depressieve_klachten: false,
        cardiale_problematiek: false,
        diabetes_mellitus: false,
        hoge_bloeddruk: false,
        anders_text: null,
        anders_checked: false,
    };
}

export function ksyosMedicalCenterCustomControlDataFieldsInitialize() {
    const customControlDataInitialized: IKsyosMedicalCenterCustomControlDataFields = {
        dateConsult: DateTime.now().format('YYYY-MM-DD'),
        aanvullendeAnamnese: null,
        aanvullendLichamelijkOnderzoek: null,
        gemiddeldeAhi: null,
        gemiddeldeOdi: null,
        odi4: null,
        gemiddeldeSaturatie: null,
        laagstGemetenSaturatie: null,
        relevanteHypoxemie: null,
        relevanteHypoxemieRemarks: null,
        positieafhankelijk: null,
        positieafhankelijkRemarks: null,
        inefficienteSlaaphygiene: false,
        inefficienteSlaaphygieneRemarks: null,
        veelBewegingen: false,
        slechteSlaapefficientie: false,
        afwijkendHartritme: false,
        afwijkendHartritmeRemarks: null,
        overigeRelevanteBevindingenAndersText: null,
        osa: null,
        behandeling1eLijnRemark: null,
        ...initializeOsaYesFlow(),
        bmiCategorie: null,
        comorbiditeitenVolgensHuisarts: initializeComorbiditeitenVolgensHuisarts(),
        typeSlaaprecorder: '',
        emailAddress: '',
    };

    return customControlDataInitialized;
}
